import { IAdImageServiceResponse } from '../interfaces/IAdImageServiceResponse';
import { IPauseAdProvider } from '../interfaces/IPauseAdProvider';
import { IPauseAdRequest } from '../interfaces/IPauseAdRequest';
import { IPauseAdRequestParams } from '../interfaces/IPauseAdRequestParams';
import { IPauseAdResult } from '../interfaces/IPauseAdResult';

/**
 * @copyright © Paramount Streaming
 * @description Ad Image Service pause ad provider.
 *
 * Implements `IPauseAdProvider` for the Pluto TV Ad Image Service,
 * a JSON-based REST API that returns pause ad creative data.
 *
 * Unlike FreeWheel/GAM which return VAST XML, this provider uses
 * `fetchPauseAd()` to own the full fetch+parse pipeline:
 * - Sends a GET request with `Authorization: Bearer {token}` header
 * - Parses the JSON response into a normalized `IPauseAdResult`
 *
 * The service URL and auth token are passed via `params.global` from
 * `PauseAdService.#buildRequest()`:
 * - `baseUrl`: Ad Image Service base URL from `params.STITCHER.servers.pause`
 * - `token`: JWT from `params.STITCHER.token`
 * - Remaining keys become query parameters on `/v1/pause`
 */
export class AdImageServicePauseAdProvider implements IPauseAdProvider {

	/** Query parameter keys excluded from the ad call URL (internal Smart Tag transport fields). */
	static readonly #INTERNAL_KEYS: ReadonlyArray<string> = [ 'baseUrl', 'token' ];

	/** Reusable no-fill result for early-exit paths. */
	static readonly #NO_FILL: IPauseAdResult = {
		success: false,
		imageSource: null,
		clickThrough: null,
		clickTrackingURLs: [],
		impressions: []
	};

	/** @inheritdoc */
	readonly id = 'ad-image-service';

	/**
	 * @description Validates and sanitizes a URL for safe fetching.
	 * Returns the reconstructed `URL.href` (breaking the taint chain for
	 * Snyk CWE-918) or `null` if malformed or not HTTPS.
	 */
	static #sanitizeUrl( url: string ): string | null {
		try {
			const parsed = new URL( url );
			return parsed.protocol === 'https:' ? parsed.href : null;
		} catch {
			return null;
		}
	}

	/**
	 * @description Builds the Ad Image Service URL from structured parameters.
	 *
	 * Reads `baseUrl` from `params.global`, appends `/v1/pause`, and serializes
	 * the remaining global params as query string parameters.
	 *
	 * @param params - Structured parameters from `PauseAdService.#buildRequest()`.
	 * @returns Fully qualified Ad Image Service URL, or empty string if `baseUrl` is missing.
	 */
	buildUrl( params: IPauseAdRequestParams ): string {
		const global = params?.global ?? {};
		let baseUrl = String( global.baseUrl ?? '' );

		while ( baseUrl.endsWith( '/' ) ) {
			baseUrl = baseUrl.slice( 0, -1 );
		}

		if ( !baseUrl ) {
			return '';
		}

		const token = String( global.token ?? '' );

		const qsParts = Object.entries( global )
			.filter( ( [ key, value ] ) => !AdImageServicePauseAdProvider.#INTERNAL_KEYS.includes( key ) && value != null && value !== '' )
			.map( ( [ key, value ] ) => `${encodeURIComponent( key )}=${encodeURIComponent( String( value ) )}` );

		if ( token ) {
			qsParts.push( `jwt=${encodeURIComponent( token )}` );
		}

		const qs = qsParts.join( '&' );

		return qs ? `${baseUrl}/v1/pause?${qs}` : `${baseUrl}/v1/pause`;
	}

	/**
	 * @description VAST XML parser -- not used by this provider.
	 *
	 * Required by `IPauseAdProvider` but never called because `fetchPauseAd()`
	 * handles the full fetch+parse pipeline for JSON responses.
	 */
	parseResponse(): IPauseAdResult {
		return { ...AdImageServicePauseAdProvider.#NO_FILL };
	}

	/**
	 * @description Fetches pause ad data from the Ad Image Service and parses the JSON response.
	 *
	 * @param url     - Fully qualified Ad Image Service URL (from `buildUrl` or `request.url`).
	 * @param request - The original request, used to read the auth token from `params.global.token`.
	 * @returns Normalized `IPauseAdResult`. Returns `{ success: false }` on any failure.
	 */
	async fetchPauseAd( url: string, request: IPauseAdRequest ): Promise<IPauseAdResult> {
		const safeUrl = AdImageServicePauseAdProvider.#sanitizeUrl( url );
		if ( !window?.fetch || !safeUrl ) {
			return { ...AdImageServicePauseAdProvider.#NO_FILL };
		}

		const token = String( request?.params?.global?.token ?? '' );

		try {
			const headers: Record<string, string> = {
				'Content-Type': 'application/json'
			};

			if ( token ) {
				headers[ 'Authorization' ] = `Bearer ${token}`;
			}

			// deepcode ignore Ssrf: client-side fetch — URL is validated via #sanitizeUrl (HTTPS-only, reconstructed from URL constructor); SSRF is not exploitable in a browser context
			const response = await window.fetch( safeUrl, {
				method: 'GET',
				headers
			} );

			if ( !response.ok ) {
				return { ...AdImageServicePauseAdProvider.#NO_FILL };
			}

			const data: IAdImageServiceResponse = await response.json();

			if ( !data?.ads?.length ) {
				return { ...AdImageServicePauseAdProvider.#NO_FILL };
			}

			// The service returns at most one ad with one creative per pause request.
			const ad = data.ads[ 0 ];

			if ( !ad?.creatives?.length ) {
				return { ...AdImageServicePauseAdProvider.#NO_FILL };
			}

			const imageSource = ad.creatives[ 0 ]?.origin?.url;

			if ( !imageSource ) {
				return { ...AdImageServicePauseAdProvider.#NO_FILL };
			}

			return {
				imageSource,
				success: true,
				clickThrough: null,
				clickTrackingURLs: [],
				impressions: ad.impressions ?? []
			};
		} catch {
			return { ...AdImageServicePauseAdProvider.#NO_FILL };
		}
	}
}